/**
 * The session's server-side history: the `session-history` query, the effect
 * that seeds the transcript from it once per session, and what the read has to
 * say for itself. Split out of `useAgentChat.ts` with no behavior change.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  attachPendingChoices,
  attachPendingConfirmations,
  isRefusalNotice,
  type ChatMessage,
} from './agentChatReducer';
import {
  getSessionHistory,
  type HistoryMessage,
  type SessionHistoryResult,
} from '../../services/agentChat.service';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { isStoppingOwnTurn, type ChatCore } from './useAgentChatCore';
import type { ChatHistoryState, TurnStillWorking } from './useAgentChat.types';

/**
 * How often the transcript is re-read while another request's turn runs.
 *
 * The one data poll the "events, never polling" sweep (2026-10-23) left in
 * place, deliberately. The turn runs in the agent service, which has no
 * Socket.IO path: its turn lock is a Postgres advisory lock nothing broadcasts,
 * and a push would need a new agent-to-backend call made when the turn ends,
 * authenticated by a user token that a long turn can outlive. It is bounded
 * (only while a turn this tab did not start is running, never while this tab
 * streams, and never past `STILL_WORKING_CAP_MS`). Replacing it is that
 * cross-service push, not a change to this hook alone.
 */
export const STILL_WORKING_POLL_MS = 4_000;
/**
 * When to stop waiting for it. Far past any healthy turn; past it the chat
 * unlocks and says to refresh, rather than spinning forever (rule 12).
 */
export const STILL_WORKING_CAP_MS = 10 * 60 * 1_000;

export interface ChatHistoryArgs {
  readonly chat: ChatCore;
  readonly enabled: boolean;
  readonly workspaceId: string | null;
  readonly sessionId: string;
  readonly messages: readonly ChatMessage[];
  /** This tab's own turn is streaming; it, not the history, is the truth then. */
  readonly isStreaming: boolean;
}

export interface ChatHistory {
  readonly isLoadingHistory: boolean;
  readonly historyState: ChatHistoryState;
  readonly retryHistory: () => void;
  readonly turnStillWorking: TurnStillWorking;
}

/**
 * Whether the seed effect copies nothing this run. The copy is once per
 * session, except while following a turn another request runs; and even then
 * a snapshot identical to the last one applied copies nothing, so the
 * transcript (and the panel's scroll) stays put. A refusal still re-seeds, to
 * drop its notice on that snapshot.
 */
function skipsSeed(state: {
  readonly firstSeed: boolean;
  readonly following: boolean;
  readonly unchanged: boolean;
  readonly refusalPending: boolean;
}): boolean {
  if (state.firstSeed) return false;
  if (!state.following) return true;
  return state.unchanged && !state.refusalPending;
}

/**
 * What one read says about a turn running elsewhere: true, false, or null when
 * it could not find out (the server's probe failed or was slow, or the read
 * itself failed). Not found and forbidden are definitive: nothing to follow.
 */
function turnAnswer(data: SessionHistoryResult | undefined): boolean | null {
  // Nothing read yet: nothing to follow, and no read time to start a wait on.
  if (!data) return false;
  if (data.error === 'error') return null;
  if (data.error !== null) return false;
  return data.turnInProgress;
}

/**
 * A read's answer, or, when it could not say, the answer the session last had:
 * it was being followed, or a refusal just said a turn is running.
 */
function stillRunning(answer: boolean | null, wasFollowing: boolean, refused: boolean): boolean {
  return answer ?? (wasFollowing || refused);
}

/**
 * What an applied read settles beyond the copy itself.
 *
 * A read that says nothing is running means a Stop this tab pressed on the
 * session has been let go. And the first-seed impression: the history showed
 * a turn running when this tab loaded the conversation (another tab or
 * device, or the turn a reload left running). A refusal in THIS tab
 * already reported itself (`refuseTurn`), and its read can be the session's
 * FIRST seed (sent while the first read was in flight, or after it failed),
 * so a seed the refusal asked for is not counted a second time; nor is this
 * tab's own stopping turn.
 */
function settleAppliedRead(read: {
  readonly chat: ChatCore;
  readonly sessionId: string;
  readonly answer: boolean | null;
  readonly remoteTurn: boolean;
  readonly firstSeed: boolean;
  readonly refusalPending: boolean;
}): void {
  const { chat, sessionId } = read;
  if (read.answer === false) chat.stoppedOwnTurnRef.current = null;
  if (!read.remoteTurn || !read.firstSeed || read.refusalPending) return;
  if (isStoppingOwnTurn(chat.stoppedOwnTurnRef.current, sessionId, Date.now())) return;
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TURN_STILL_WORKING_SHOWN, {
    reason: 'opened_mid_turn',
  });
}

/**
 * Whether a row on screen survives a re-seed: the server's snapshot cannot
 * carry it. An unacknowledged turn row; and every other local status line (a
 * job outcome, `useAgentChatJobOutcomes`), which the server never stores. A
 * refusal notice only while the turn it waits on runs. And the reply this tab
 * stopped (`stoppedReplyRef`), by its row and for the session's life: the run
 * broke off before the server stored it, so no snapshot has it.
 */
function keptAcrossSeed(
  message: ChatMessage,
  unacknowledged: ReadonlySet<string>,
  keep: { readonly dropRefusalNotices: boolean; readonly stoppedReplyId: string | null },
): boolean {
  if (isRefusalNotice(message)) return unacknowledged.has(message.id) && !keep.dropRefusalNotices;
  if (message.id === keep.stoppedReplyId) return true;
  return unacknowledged.has(message.id) || message.role === 'status';
}

export function useAgentChatHistory({
  chat,
  enabled,
  workspaceId,
  sessionId,
  messages,
  isStreaming,
}: ChatHistoryArgs): ChatHistory {
  const { clientMintedRef, seededSessionRef } = chat;
  /**
   * The session whose last applied snapshot had a turn running elsewhere. While
   * it is set, every new snapshot re-seeds (the copy is otherwise once per
   * session), so the answer appears when it lands, including on the read that
   * reports the turn finished.
   */
  const followingSessionRef = useRef<string | null>(null);
  /**
   * The history data last copied in. While following, the effect re-runs on
   * every poll (its `isFetching` flips), and TanStack keeps the SAME object
   * for an unchanged answer (structural sharing). Re-seeding that would hand
   * the panel a new messages array every 4 s, and its auto-scroll would yank
   * the researcher to the bottom each time for up to 10 minutes.
   */
  const lastSeededDataRef = useRef<SessionHistoryResult | undefined>(undefined);
  /**
   * When this session's wait started, as the `dataUpdatedAt` of the first read
   * that reported the turn. Measured in read times, so no timer is involved:
   * the read that crosses the cap is the one that stops the polling.
   */
  const waitStartedRef = useRef<{ readonly sessionId: string; readonly at: number } | null>(null);
  /**
   * Whether a turn runs elsewhere, by this read: its answer, or when it could
   * not say (`turnAnswer` null), the answer this session last had. An unknown
   * or failed read mid-wait keeps the wait, and its polling, going; ending it
   * would unlock the chat over a turn that may still be running, with no read
   * left to bring the answer in.
   */
  const isRunning = (data: SessionHistoryResult | undefined): boolean =>
    stillRunning(
      turnAnswer(data),
      followingSessionRef.current === sessionId,
      chat.refusalPendingRef.current,
    );
  const waitedTooLong = (data: SessionHistoryResult | undefined, updatedAt: number): boolean => {
    const started = waitStartedRef.current;
    return (
      isRunning(data) &&
      started?.sessionId === sessionId &&
      updatedAt - started.at >= STILL_WORKING_CAP_MS
    );
  };
  // Server-side session history — owned by TanStack Query so dedup,
  // background-refetch, and cache lifecycle come for free. The hook's
  // local ``messages`` state is a *copy* of the loaded history; once
  // streaming starts it diverges (the SSE handler appends + mutates
  // messages directly, which a TanStack cache value can't model).
  // Seeding happens in the effect below, gated on ``seededSessionRef``
  // so we copy the cached value exactly once per session — re-fires
  // (e.g. tab refocus) don't clobber in-flight chat state.
  const historyQuery = useQuery({
    queryKey: ['session-history', workspaceId, sessionId],
    queryFn: () => getSessionHistory(sessionId),
    // Skip ids this client minted: the row doesn't exist server-side until the
    // first message, so the fetch is a certain 404 that only buys a spinner
    // where the empty state (suggested prompts) should render immediately.
    enabled: enabled && !!workspaceId && !!sessionId && !clientMintedRef.current.has(sessionId),
    staleTime: 60 * 1000,
    retry: false,
    // While another request's turn runs on this session, re-read until it
    // lands (or the cap passes). Never while this tab streams its own turn:
    // the server would report that turn, and this tab already shows it.
    refetchInterval: (query) =>
      !isStreaming &&
      isRunning(query.state.data) &&
      !waitedTooLong(query.state.data, query.state.dataUpdatedAt)
        ? STILL_WORKING_POLL_MS
        : false,
  });

  const turnInProgress = !isStreaming && isRunning(historyQuery.data);
  useEffect(() => {
    if (!turnInProgress) {
      waitStartedRef.current = null;
      return;
    }
    if (waitStartedRef.current?.sessionId !== sessionId) {
      waitStartedRef.current = { sessionId, at: historyQuery.dataUpdatedAt };
    }
  }, [turnInProgress, sessionId, historyQuery.dataUpdatedAt]);

  useEffect(() => {
    // An INACTIVE session has nothing to seed from, and must not record that it
    // did. `enabled` is false while `AgentChatProvider` holds the session at app
    // root with no chat surface mounted, and a disabled query reports
    // `isLoading: false` with `data: undefined` — indistinguishable below from
    // "asked, and the server has no such session". That is what emptied the
    // panel for every returning researcher before this line existed.
    //
    // REDUNDANT as the code now stands, and kept deliberately: the "could not
    // find out" return below reaches the same conclusion by a different route,
    // so NO test distinguishes this line — removing it reds nothing. Do not
    // read that green as coverage. It is kept because it states the more
    // fundamental fact (nothing was asked, so nothing is known), and because it
    // is the backstop if that branch is ever tightened back to stamping, which
    // is an easy-looking simplification with no failing test to stop it.
    if (!enabled) return;
    // This tab's own turn is the truth while it streams: whatever was being
    // followed before (a wait given up at the cap) is over. Left set, the
    // Stop that ends this turn would re-seed from the snapshot taken before
    // the send, deleting the message and its partial answer. A refusal arms
    // the follow again (`refusalPendingRef`), so a refusal still waiting on
    // its read is over too: this tab resent, and its own turn is the truth.
    if (isStreaming) {
      followingSessionRef.current = null;
      chat.refusalPendingRef.current = false;
    }
    const firstSeed = chat.seededSessionRef.current !== sessionId;
    // The copy is once per session EXCEPT while following a turn another
    // request is running: then every snapshot re-seeds, through the one that
    // says it finished. Never while this tab streams its own turn: that is
    // the turn the server would be reporting.
    const refusalPending = chat.refusalPendingRef.current;
    const following =
      !isStreaming &&
      (followingSessionRef.current === sessionId ||
        historyQuery.data?.turnInProgress === true ||
        refusalPending);
    const unchanged = historyQuery.data === lastSeededDataRef.current;
    if (skipsSeed({ firstSeed, following, unchanged, refusalPending })) return;
    /**
     * Copy the server's snapshot in, WITHOUT discarding a turn that is newer
     * than it.
     *
     * The composer is live while this request is in flight (`sendMessage` does
     * not gate on `isLoadingHistory`), so a researcher can send before the
     * transcript arrives. The snapshot was taken before that send, so a plain
     * replacing `setMessages` deletes their message and the assistant
     * placeholder with no error — while the turn keeps running server-side.
     * `createNewSession` already mitigates this for a CLIENT-MINTED id by
     * pre-stamping `seededSessionRef`; an id restored from localStorage, which
     * is every returning researcher, took the unguarded path.
     *
     * What is kept is `unacknowledgedRowIdsRef`, not "is `prev` non-empty" and
     * not "the newest turn": the predicate is the rows the SERVER has not
     * acknowledged, which is exactly what a snapshot cannot already contain.
     * Once a turn is acknowledged, `done` invalidates the cache and the
     * response that arrives DOES carry it — keeping it as well would show the
     * question twice.
     */
    // Snapshot the set here, not inside the updater: the updater may run later
    // than this effect, and the ref is mutated by every send and every terminal
    // frame. The pointer this replaced was captured by value for the same
    // reason.
    const unacknowledged = new Set(chat.unacknowledgedRowIdsRef.current);
    const stopped = chat.stoppedReplyRef.current;
    const stoppedReplyId = stopped?.sessionId === sessionId ? stopped.id : null;
    const seed = (rows: ChatMessage[], dropRefusalNotices = false) => {
      // Replace the transcript with the server's, then re-append exactly the
      // rows it cannot know about. Keeping any MORE than that showed a
      // completed turn twice, permanently, because this pass stamps; keeping
      // any fewer deleted something a person wrote.
      //
      // Kept rows go AFTER the server's, in their own original order. Putting
      // an older failed turn back in its true position would mean interleaving
      // two different clocks — the server's coarse seconds against local
      // milliseconds — and misordering a transcript is worse than appending to
      // it. The bottom is also where the turn that needs attention belongs,
      // next to the composer.
      //
      // No empty-set short circuit: it only avoided an allocation, produced an
      // identical render, and so was a branch no test could ever distinguish.
      //
      // A refusal notice is kept only while the turn it waits on is running:
      // the snapshot that shows nothing running already carries that answer,
      // and "still working" under it would be false.
      chat.setMessages((prev) => [
        ...rows,
        ...prev.filter((m) =>
          keptAcrossSeed(m, unacknowledged, { dropRefusalNotices, stoppedReplyId }),
        ),
      ]);
    };
    // ``isLoading`` (= isPending && isFetching), NOT ``isPending`` — same trap
    // as isLoadingHistory below: a DISABLED query is pending forever, so
    // keying on isPending would make this effect early-return for every
    // client-minted id and leave the previously-open thread's messages on
    // screen under the new session's id.
    if (historyQuery.isLoading) return;
    // Nor while a REVALIDATION is in flight. `isLoading` is false the moment
    // there is any cached value, so without this the effect seeds from an
    // entry we have just invalidated and then never re-seeds — the copy is
    // once-per-session by design. That is destructive for a paused tool call:
    // an answered one would come back as a live control, and ADK re-executes
    // on a second answer. A fresh mount with no cache is unaffected (it is
    // still `isLoading`), and so is a mount whose cache is fresh.
    if (historyQuery.isFetching) return;
    if (!historyQuery.data) {
      if (chat.clientMintedRef.current.has(sessionId)) {
        // Disabled BY DESIGN, not failed: the server has no row for an id this
        // client minted, so "no data" is a definitive empty. Stamping is what
        // stops the PREVIOUS thread's transcript staying on screen under this
        // id when the user switches back to it (the #887 adversarial finding,
        // pinned by useAgentChat.createNewSession.test.tsx).
        chat.seededSessionRef.current = sessionId;
        seed([]);
        return;
      }
      // "We could not find out", which is NOT "there is nothing". Record
      // nothing and change nothing: stamping `seededSessionRef` here would
      // forfeit the session for good, because the copy is once-per-session by
      // design and a later successful fetch would find it already stamped.
      // That was survivable while this hook had the PANEL's lifetime — closing
      // and reopening the chat built a fresh instance and the next fetch seeded
      // it — but AgentChatProvider holds it for the whole page, so one
      // transient failure would empty the conversation until a reload.
      //
      // Not stamping cannot spin: this effect is dep-driven, so it re-runs only
      // when `data` / `isFetching` actually change — i.e. when someone asks
      // again (a finished turn's `invalidateHistory`, a staleTime refetch).
      return;
    }
    const result = historyQuery.data;
    if (result.error === 'error') {
      // Same argument, one layer in: `getSessionHistory` never rejects, it
      // RESOLVES this shape for a 5xx, a dropped connection or a missing auth
      // header. A failed read is not an empty conversation.
      return;
    }
    if (result.error === 'not_found' || result.error === null) {
      const loaded: ChatMessage[] = result.messages.map((m: HistoryMessage, i: number) => ({
        // Prefer the server's ADK event id: it is stable across reloads and is
        // what a chat-search result points at, so it doubles as the scroll
        // anchor for deep-linking. The index-based id remains the fallback for
        // a message the server sent without one.
        id: m.message_id ?? `history-${i}-${m.timestamp ?? i}`,
        role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.content,
        author: m.author,
        timestamp: m.timestamp ? m.timestamp * 1000 : Date.now(),
        sources: m.sources,
        attachments: m.attachments,
        spreadsheetChanges: m.spreadsheet_changes,
      }));
      // Calls ADK is still paused on come back as a control, not as a
      // transcript row — see attachPendingConfirmations for where it lands.
      // Confirmations first: that call decides whether a carrier row is
      // needed, and the choice pass appends into whatever it produced. The
      // other order mints two carriers for one reload.
      // An unknown answer keeps the one before it (`isRunning`): it neither
      // drops the notice nor ends the wait.
      const running = stillRunning(
        result.turnInProgress,
        followingSessionRef.current === sessionId,
        refusalPending,
      );
      seed(
        attachPendingChoices(
          attachPendingConfirmations(loaded, result.pendingConfirmations),
          result.pendingChoices,
        ),
        !running,
      );
      chat.refusalPendingRef.current = false;
      const remoteTurn = running && !isStreaming;
      settleAppliedRead({
        chat,
        sessionId,
        answer: result.turnInProgress,
        remoteTurn,
        firstSeed,
        refusalPending,
      });
      followingSessionRef.current = remoteTurn ? sessionId : null;
      lastSeededDataRef.current = result;
    } else {
      // 'forbidden': a DEFINITIVE answer — this session is not this user's to
      // read — so unlike the failure branches above it does settle the
      // question, and stamping below is right. ``historyState`` reports it as
      // the terminal kind, which is what lets the panel replace the pane here
      // and merely warn on the transient failures above.
      seed([]);
    }
    chat.seededSessionRef.current = sessionId;
    // Depend on what the body actually reads. This tracked `isPending` while
    // the guard above still used it; leaving it stale would be worse than a
    // lint warning — for a disabled query `isPending` is permanently true and
    // never re-triggers, so the effect could miss the transition it exists to
    // observe.
  }, [
    chat,
    enabled,
    sessionId,
    historyQuery.data,
    historyQuery.isLoading,
    historyQuery.isFetching,
    isStreaming,
  ]);

  // ``isLoading`` (= isPending && isFetching), NOT ``isPending``: a disabled
  // query stays pending forever, so keying the spinner off isPending would pin
  // it permanently on every client-minted session.
  // "Switched, not yet seeded" is a loading state too. `isLoading` is
  // `isPending && isFetching`, and a session whose cache entry exists but is
  // STALE is not pending — so on a switch into one, this read `false` while the
  // refetch ran, and `switchSession` now clears `messages` on the way in. The
  // panel fell past the spinner to its `messages.length === 0` branch and drew
  // "Start a conversation with Bower" plus the empty-state prompts over a
  // conversation that was on its way. The handover's phase-2 gate reads this
  // too, and waiting for the refetch is what it wants.
  //
  // `messages.length === 0` is not belt-and-braces. `getMessagesContent()`
  // checks this flag BEFORE `messages`, and a session stays unseeded after a
  // failed read while every turn ends in `invalidateHistory()` — so without it
  // the finished conversation is replaced by a bare spinner on every turn. Only
  // claim to be loading when there is nothing to show.
  const isLoadingHistory =
    (historyQuery.isLoading ||
      (seededSessionRef.current !== sessionId &&
        historyQuery.isFetching &&
        messages.length === 0)) &&
    !!workspaceId &&
    !!sessionId;
  // `isError` is UNREACHABLE as the code stands, and is kept deliberately —
  // the same bargain, and for the same reason, as the `if (!enabled) return`
  // at the top of the seed effect.
  //
  // An earlier version of this comment justified it as "`buildHeaders()` is
  // awaited outside the try, so a rejection there arrives as a query error".
  // That is false: its only await is `authService.getAuthToken()`, which
  // catches and returns null, and `getWorkspaceId()` is a module read. So
  // `getSessionHistory` really never rejects, every route resolves, and the
  // first half covers all of them. NO test can distinguish this clause; do not
  // read its green as coverage.
  //
  // It stays because the seed effect ALSO declines to write on a data-less
  // non-minted read, and the two must agree about what that means. If the
  // service ever gains a throwing path, this is what keeps the decline from
  // being silent again — which is the whole bug being fixed here.
  const historyReadFailed = historyQuery.data?.error === 'error' || historyQuery.isError;
  let historyState: ChatHistoryState = { kind: 'ok' };
  if (historyQuery.data?.error === 'forbidden') {
    historyState = { kind: 'forbidden', message: 'Not authorized to view this session' };
  } else if (historyReadFailed) {
    historyState = { kind: 'load_failed', isRetrying: historyQuery.isFetching };
  }

  const { refetch: refetchHistory } = historyQuery;
  const retryHistory = useCallback(() => {
    void refetchHistory();
  }, [refetchHistory]);

  // A refusal whose read has not landed yet: the server just said a turn is
  // running, so the composer stays locked until that read answers.
  const awaitingRefusalRead = !isStreaming && chat.refusalPendingRef.current;
  const running = turnInProgress || awaitingRefusalRead;
  let turnStillWorking: TurnStillWorking = 'none';
  if (running && isStoppingOwnTurn(chat.stoppedOwnTurnRef.current, sessionId, Date.now())) {
    // This tab's own stopped turn, still holding the session.
    turnStillWorking = 'stopping';
  } else if (turnInProgress) {
    turnStillWorking = waitedTooLong(historyQuery.data, historyQuery.dataUpdatedAt)
      ? 'gave_up'
      : 'working';
  } else if (awaitingRefusalRead) {
    turnStillWorking = 'working';
  }

  return { isLoadingHistory, historyState, retryHistory, turnStillWorking };
}
